import { useQuery } from '@apollo/client';
import { formatTimeRangeToISO } from "../../../../utils/dateTimeUtils";
import { 
    GET_ORDER_LISTING_FOR_CENTRAL,
    CentralOrderListResponse
} from '../graphql/queries/getOrderListingForCentral';
import { OrderFiltersType } from "../../../../graphql/types/OrderFiltersType";
import { OrderType, AddressType } from "../../../../graphql/types/OrderType";
import { useWarehouseContext } from "../../../../contexts/WarehouseContext";

// 工具函数：构建完整地址字符串
function buildFullAddress(address?: AddressType): string | undefined {
    if (!address) return undefined;
    
    const parts: string[] = [];
    
    // 街道地址 + 建筑/单元号
    if (address.addressLine1) {
        let addressLine = address.addressLine1;
        if (address.addressLine2) {
            addressLine += `, ${address.addressLine2}`;
        }
        parts.push(addressLine);
    }
    
    // 城市, 州 邮编
    if (address.city && address.state && address.zipcode) {
        parts.push(`${address.city}, ${address.state} ${address.zipcode}`);
    }
    
    return parts.length > 0 ? parts.join(', ') : undefined;
}

// Central异常页面专用的订单类型，扩展OrderType添加完整地址字符串
export interface OrderWithAddressType extends OrderType {
    receiverFullAddress?: string;
}

// 转换函数：为OrderType添加完整地址字符串
export function addFullAddressToOrder(order: OrderType): OrderWithAddressType {
    return {
        ...order,
        receiverFullAddress: buildFullAddress(order.recipientAddress),
    };
}

export interface UseCentralExceptionQueryResult {
    parcelData: OrderWithAddressType[];
    loading: boolean;
    error: any;
    refetch: (variables?: any) => void;
    totalElements: number;
    accessiblePages: number;
    buildQueryVariables: () => any;
}

export const useCentralExceptionQuery = (
    filters: OrderFiltersType,
    currentPage: number,
    pageSize: number
): UseCentralExceptionQueryResult => {
    // 获取仓库信息进行数据隔离
    const { currentWarehouse } = useWarehouseContext();
    const sortingCodes = currentWarehouse?.sortingCode?.split('-') || [];

    const buildQueryVariables = () => {
        //构建分拣码段过滤器实现数据隔离
        const sortingFilters: Partial<OrderFiltersType> = {};
        if (sortingCodes.length > 0) {
            sortingFilters.sortingCodeSegment1 = sortingCodes[0] || undefined;
            if (sortingCodes.length > 1) {
                sortingFilters.sortingCodeSegment2 = sortingCodes[1] || undefined;
                if (sortingCodes.length > 2) {
                    sortingFilters.sortingCodeSegment3 = sortingCodes[2] || undefined;
                }
            }
        }

        return {
            filters: {
                trackingNumbers: filters.trackingNumbers?.toString() || undefined,
                channelId: filters.channelId?.[0] ? parseInt(filters.channelId[0]) : undefined,
                overallStatuses: filters.overallStatus && filters.overallStatus.length > 0 ? filters.overallStatus : undefined,
                exceptionReasonCode: filters.exceptionReasonCode || undefined,
                exceptionReasonCodes: filters.exceptionReasonCodes || undefined,
                markExceptionAt: formatTimeRangeToISO(filters.markExceptionAt),
                isAbnormal: filters.isAbnormal,
                exceptionStatuses: filters.exceptionStatuses || undefined,
                nextAction: filters.nextAction || undefined,
                exceptionWarehouseId: filters.exceptionWarehouseId || undefined,
                exceptionHubId: filters.exceptionHubId || undefined,
                isHasBeenSetException: true, // Central异常页面固定只显示已标记过异常的订单
                // 关键：添加分拣码段过滤实现数据隔离
                ...sortingFilters
            },
            pageRequest: {
                page: currentPage,
                size: pageSize,
                sortBy: 'markExceptionAt',
                sortDirection: 'DESC'
            }
        };
    };

    const { data, loading, error, refetch } = useQuery<CentralOrderListResponse>(
        GET_ORDER_LISTING_FOR_CENTRAL,
        {
            variables: buildQueryVariables(),
            fetchPolicy: 'cache-and-network'
        }
    );

    // 为订单数据添加完整地址字符串
    const parcelData: OrderWithAddressType[] = data?.getOrderListingForCentral?.content
        ? data.getOrderListingForCentral.content.map(addFullAddressToOrder)
        : [];

    const totalElements = data?.getOrderListingForCentral?.totalElements || 0;
    const accessiblePages = data?.getOrderListingForCentral?.accessiblePages || 0;

    return {
        parcelData,
        loading,
        error,
        refetch,
        totalElements,
        accessiblePages,
        buildQueryVariables
    };
};